import Delta from 'quill-delta';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ArrowLeftIcon,
	DialogueIcon,
	FieldContainer,
	FlexGap,
	IconButton,
	Input,
	Label,
	Modal,
	Paragraph,
	QuillInput,
	QuillText,
	tokens,
} from '@treams/treams-ui';

import FeedbackComposer from '../composer/FeedbackComposer';
import type { FeedbackCompetencySelection } from '../composer/FeedbackComposerCompetencies';
import FeedbackRequestProgress from './FeedbackRequestProgress';
import { hasRequiredQuestions } from './FeedbackRequestValidation';
import FeedbackTemplateSelector from './FeedbackTemplateSelector';

const Error = styled(Box)`
	color: ${(props) => props.theme.tokens.error.text};
`;

export default function FeedbackRequestModalQuestions(props: {
	method: 'template' | 'custom' | 'competencies' | 'goals';
	userID: string;
	instruction?: Delta;
	needsTemplate?: 'optional' | 'required';
	title?: string;
	template?: graphql.FeedbackTemplate;
	questions: (graphql.FeedbackRequestQuestionInput & { id: string })[];
	goals: { goalID: string; text?: string; order: number }[];
	competencies: FeedbackCompetencySelection[];
	onChange: (
		questions: (graphql.FeedbackRequestQuestionInput & { id: string })[],
		goals: { goalID: string; text?: string; order: number }[],
		competencies: FeedbackCompetencySelection[],
		title?: string,
		instruction?: Delta,
		template?: graphql.FeedbackTemplate,
	) => void;
	onBack: () => void;
	onDone: () => void;
	returnURL: string;
	minimumQuestions?: number;
	templateLocked?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [shouldValidate, setShouldValidate] = useState(false);

	const onChange = useCallback(
		(
			questions: (graphql.FeedbackRequestQuestionInput & { id: string })[],
			goals: { goalID: string; text?: string; order: number }[],
			competencies: FeedbackCompetencySelection[],
			title?: string,
			instruction?: Delta,
			template?: graphql.FeedbackTemplate,
		) => {
			setShouldValidate(false);
			props.onChange(questions, goals, competencies, title, instruction, template);
		},
		[props.onChange],
	);

	const validationError = hasRequiredQuestions(
		props.questions,
		props.goals,
		props.competencies,
		props.minimumQuestions ?? 1,
	);

	const blockingError =
		props.templateLocked && validationError === 'notMeetingMinimum' ? undefined : validationError;

	return (
		<Modal>
			<Modal.Header
				leftAccessory={
					<IconButton icon={<ArrowLeftIcon />} type="secondary" onClick={props.onBack} />
				}
				icon={<DialogueIcon size={24} />}
				title={t('common.feedback.setupQueriesFromScratch')}
				closeLink={props.returnURL}
			/>
			<FeedbackRequestProgress step={1} />
			<Modal.Contents pt={0}>
				<FlexGap flexDirection="column" gap={tokens.gap.group}>
					{props.needsTemplate && (
						<FieldContainer>
							<FeedbackTemplateSelector
								template={props.template}
								userID={props.userID}
								disabled={props.templateLocked}
								onChange={(template) =>
									onChange(
										template?.questions.map(({ question, canSubjectView }) => ({
											id: Math.random().toString(),
											type: question.type,
											text: localize(question.text).text,
											localizedText: question.text,
											subTitle: localize(question.subTitle ?? [])?.text,
											additionalDetails: localize(question.additionalDetails ?? [])?.text,
											categoryID: question.category.id,
											canSubjectView,
											options: (question.options ?? []).map((option) => ({
												id: option.id,
												text: option.text,
											})),
										})) ?? [],
										props.goals,
										props.competencies,
										props.title,
										props.instruction,
										template,
									)
								}
								method={props.method}
							/>
							{props.template?.description && (
								<Paragraph mt={2}>
									<QuillText value={localize(props.template.description)?.text} />
								</Paragraph>
							)}
						</FieldContainer>
					)}
					<FieldContainer>
						<Label mb={2}>{t('common.feedback.composer.title')}</Label>
						<Input
							value={
								props.title ?? (props.template ? localize(props.template.title).text : undefined)
							}
							onChange={(value) =>
								onChange(
									props.questions,
									props.goals,
									props.competencies,
									value,
									props.instruction,
									props.template,
								)
							}
						/>
					</FieldContainer>

					<FieldContainer>
						<Label mb={2}>{t('common.feedback.composer.instructionalMessage')}</Label>

						<QuillInput
							// Refresh Quill input when feedback template is changed.
							// This input should have the default value of the instruction from a feedback template.
							//
							// When the cycle has a predefined instruction, this will be passed as `props.instruction`
							// and thus will have priority over the feedback template instruction.
							//
							// The behaviour is as follows: when switching feedback templates, it loads all the new values
							// whenever a value is changed, this will become `props.instruction` and thus becomes the new
							// value, even if the template is changed.
							key={props.template?.id ?? 'template.none'}
							value={
								props.instruction ||
								localize(props.template?.instruction ?? [])?.text || { ops: [] }
							}
							onChange={(value) =>
								onChange(
									props.questions,
									props.goals,
									props.competencies,
									props.title,
									value,
									props.template,
								)
							}
						/>
					</FieldContainer>

					{(!props.needsTemplate || props.needsTemplate === 'optional' || props.template) && (
						<>
							<FeedbackComposer
								method={props.method}
								userID={props.userID}
								readOnly={props.templateLocked}
								questions={props.questions}
								goals={props.goals}
								competencies={props.competencies}
								onChange={(questions, goals, competencies) =>
									onChange(
										questions,
										goals,
										competencies,
										props.title,
										props.instruction,
										props.template,
									)
								}
							/>
						</>
					)}

					{shouldValidate && (
						<>
							{validationError === 'notMeetingMinimum' && !props.templateLocked && (
								<Error mb={3}>
									{props.minimumQuestions === 0
										? t('errors.feedbackRequests.minimumQuestion')
										: t('errors.feedbackRequests.minimumQuestions', {
												minimumQuestions: props.minimumQuestions ?? 1,
											})}
								</Error>
							)}

							{validationError === 'emptyQuestions' && (
								<Error mb={3}>{t('errors.feedbackRequests.emptyQuestion')}</Error>
							)}
						</>
					)}
				</FlexGap>
			</Modal.Contents>
			<Modal.FormFooter
				onCancel={() => props.onBack()}
				saveTitle={t('common.feedback.nextStepRecipients')}
				disabled={!props.template && props.needsTemplate === 'required'}
				onSave={() => {
					if (blockingError) {
						setShouldValidate(true);
					} else {
						props.onDone();
					}
				}}
			/>
		</Modal>
	);
}
